import { createClient, Client } from '@libsql/client/web';
import { isDesktop } from '../utils/platform';
import { SCHEMA } from './schema';

export type TursoSyncStatus = 'synced' | 'syncing' | 'offline' | 'disabled' | 'error';

export interface TursoConfig {
  url: string;
  authToken: string;
  enabled: boolean;
}

// Transformar libsql:// a https:// para peticiones HTTP/REST en browsers
function normalizeTursoUrl(rawUrl: string): string {
  if (!rawUrl) return '';
  return rawUrl.startsWith('libsql://') ? rawUrl.replace('libsql://', 'https://') : rawUrl;
}

// Credenciales gestionadas automáticamente por Clerk
export function getTursoConfig(): TursoConfig {
  if (isDesktop()) {
    const envUrl = (import.meta.env.VITE_TURSO_DATABASE_URL as string) || '';
    const envToken = (import.meta.env.VITE_TURSO_AUTH_TOKEN as string) || '';
    return {
      url: envUrl,
      authToken: envToken,
      enabled: Boolean(envUrl && envToken),
    };
  }

  return {
    url: '',
    authToken: '',
    enabled: false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Estado global del cliente y suscripciones de status
// ─────────────────────────────────────────────────────────────────────────────

let clientInstance: Client | null = null;
let currentStatus: TursoSyncStatus = 'disabled';
const statusListeners = new Set<(status: TursoSyncStatus) => void>();

export function getTursoStatus(): TursoSyncStatus {
  return currentStatus;
}

export function subscribeTursoStatus(listener: (status: TursoSyncStatus) => void): () => void {
  statusListeners.add(listener);
  listener(currentStatus); // emit current state immediately
  return () => statusListeners.delete(listener);
}

function updateStatus(newStatus: TursoSyncStatus) {
  if (currentStatus === newStatus) return;
  currentStatus = newStatus;
  statusListeners.forEach(l => l(newStatus));
}

// ─────────────────────────────────────────────────────────────────────────────
// Conexión / reconexión del cliente libSQL
// ─────────────────────────────────────────────────────────────────────────────

function reconnectWithConfig(config: TursoConfig) {
  clientInstance = null;

  if (!config.enabled || !config.url || !config.authToken) {
    updateStatus('disabled');
    return;
  }

  try {
    const httpUrl = normalizeTursoUrl(config.url);
    clientInstance = createClient({ url: httpUrl, authToken: config.authToken });
    updateStatus(navigator.onLine ? 'synced' : 'offline');
    if (SCHEMA) {
      ensureTursoSchema(SCHEMA).catch(() => {});
    }
  } catch (err) {
    console.warn('[TursoSync] No se pudo instanciar el cliente:', err);
    updateStatus('error');
  }
}

/**
 * Activa la sincronización con las credenciales personales del usuario de Clerk.
 * En modo Web (isDesktop() === false), esto conecta al cliente remoto directo como fuente primaria.
 */
export function activateUserSync(dbUrl: string, dbToken: string): void {
  if (!dbUrl || !dbToken) {
    console.warn('[TursoSync] activateUserSync: credenciales vacías, ignorando.');
    return;
  }
  console.info(`[TursoSync] Activando sync (${isDesktop() ? 'Desktop Replica' : 'Web Direct Remote'}):`, dbUrl);
  reconnectWithConfig({ url: dbUrl, authToken: dbToken, enabled: true });
}

/**
 * Desactiva la sincronización con Turso.
 */
export function deactivateUserSync(): void {
  console.info('[TursoSync] Usuario deslogueado — desconectando cliente remoto.');
  clientInstance = null;
  updateStatus('disabled');
}

// ─────────────────────────────────────────────────────────────────────────────
// Inicialización al cargar el módulo
// ─────────────────────────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  if (isDesktop()) {
    const staticConfig = getTursoConfig();
    if (staticConfig.url && staticConfig.authToken && staticConfig.enabled) {
      reconnectWithConfig(staticConfig);
    } else {
      updateStatus('disabled');
    }
  } else {
    // En la Web arranca disabled hasta que el useEffect de MainLayout detecte la sesión de Clerk
    updateStatus('disabled');
  }

  window.addEventListener('online', () => {
    if (clientInstance) updateStatus('synced');
  });

  window.addEventListener('offline', () => {
    if (clientInstance) updateStatus('offline');
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// API pública de ejecución (Desktop Async / Web Direct Primary)
// ─────────────────────────────────────────────────────────────────────────────

export function getTursoClient(): Client | null {
  return clientInstance;
}

/**
 * Ejecuta una consulta SELECT directa en Turso (para modo Web cuando no hay SQLite local)
 */
export async function selectRemoteDirect<T>(query: string, params: any[] = []): Promise<T> {
  if (!clientInstance) {
    return [] as unknown as T;
  }
  try {
    updateStatus('syncing');
    const rs = await clientInstance.execute({ sql: query, args: params });
    updateStatus('synced');
    
    const rows = rs.rows.map(row => {
      const obj: Record<string, any> = {};
      rs.columns.forEach((col, idx) => {
        obj[col] = row[idx];
      });
      return obj;
    });
    return rows as unknown as T;
  } catch (err: any) {
    const errMsg = err?.message || String(err);
    if (errMsg.includes('no such table') && SCHEMA) {
      await ensureTursoSchema(SCHEMA);
      try {
        const rs = await clientInstance.execute({ sql: query, args: params });
        updateStatus('synced');
        return rs.rows.map(row => {
          const obj: Record<string, any> = {};
          rs.columns.forEach((col, idx) => { obj[col] = row[idx]; });
          return obj;
        }) as unknown as T;
      } catch (_) {}
    }
    console.error('[TursoSync] Error en selectRemoteDirect:', err);
    updateStatus('error');
    return [] as unknown as T;
  }
}

/**
 * Ejecuta una consulta de modificación en Turso.
 */
export async function syncExecuteRemote(query: string, params: any[] = []): Promise<boolean> {
  if (!clientInstance || !navigator.onLine) {
    if (!navigator.onLine && currentStatus !== 'disabled') updateStatus('offline');
    return false;
  }

  try {
    updateStatus('syncing');
    await clientInstance.execute({ sql: query, args: params });
    updateStatus('synced');
    return true;
  } catch (err: any) {
    const errMsg = err?.message || String(err);
    if (errMsg.includes('no such table') && SCHEMA) {
      await ensureTursoSchema(SCHEMA);
      try {
        await clientInstance.execute({ sql: query, args: params });
        updateStatus('synced');
        return true;
      } catch (_) {}
    }
    console.warn('[TursoSync] Fallo de ejecucion remota Turso:', err);
    updateStatus('offline');
    return false;
  }
}

/**
 * Asegura que el esquema SQL exista en la base de datos remota de Turso.
 */
export async function ensureTursoSchema(schemaSql: string): Promise<void> {
  if (!clientInstance || !navigator.onLine) return;

  try {
    updateStatus('syncing');
    const statements = schemaSql
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    for (const stmt of statements) {
      try {
        await clientInstance.execute(stmt);
      } catch (_) {
        // Ignorar tablas ya existentes
      }
    }
    updateStatus('synced');
  } catch (err) {
    console.warn('[TursoSync] No se pudo sincronizar el esquema con Turso:', err);
    updateStatus('offline');
  }
}

